import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, reactive, ref, watch } from 'vue';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import NextStepType from '@/models/NextStepType';
import UpgradedNextStep from '@/models/interfaces/UpgradedNextStep';
import { getDatepickerLocale } from '@/helpers/LocaleHelper';
import ReminderOption from '@/models/ReminderOption';
import { ProjectUnsavedDataComponents } from '@/enums/components/Project/ProjectUnsavedDataComponents';
import User from '@/models/User';
import NextStep from '@/models/NextStep';
import { transformObjectToBeBackendCompliant } from '@/helpers/NextStepHelper';
import ChatRichTextEditor from '@/shared/components/ChatRichTextEditor.vue';
import NextStepTypeRepository from '@/repositories/NextStepTypeRepository';
import { createNewChatMessageAndChat } from '@/modules/Chat/helpers/ChatHelper';
import CMSUserRepository from '@/repositories/CMSUserRepository';
import store from '@/store';

interface Props {
    isNextStepFormDisabled: boolean;
    shouldDisplayOnlyOneItem: boolean;
    shouldShowHeader?: boolean;
    isCompact?: boolean;
    chatId?: string | null;
    projectId?: string | null;
    defaultNextStepTypeName?: string | null;
    nextStepIndex: number;
    singleNextStepData: UpgradedNextStep | null;
    nextStepTypes: NextStepType[];
    reminderOptions: ReminderOption[];
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SingleNextStepForm',
  props: {
    isNextStepFormDisabled: { type: Boolean },
    shouldDisplayOnlyOneItem: { type: Boolean },
    shouldShowHeader: { type: Boolean, default: true },
    isCompact: { type: Boolean, default: false },
    chatId: { default: null },
    projectId: { default: null },
    defaultNextStepTypeName: { default: null },
    nextStepIndex: null,
    singleNextStepData: null,
    nextStepTypes: null,
    reminderOptions: null
  },
  emits: ["onDeleteNextStep"],
  setup(__props: any, { expose, emit }: { emit: ({
    (e: 'onDeleteNextStep', localKey?: number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { isNextStepFormDisabled: boolean, shouldDisplayOnlyOneItem: boolean, shouldShowHeader: boolean, isCompact: boolean, chatId: string | null, projectId: string | null, defaultNextStepTypeName: string | null, nextStepIndex: number, singleNextStepData: UpgradedNextStep | null, nextStepTypes: NextStepType[], reminderOptions: ReminderOption[] };





const { proxy } = getCurrentInstance()!;
const vm = proxy as any;

const isEditModeActive = ref(false);
const isFullHeight = ref(false);
const nextStepObject = reactive<UpgradedNextStep>({
    projectId: props.projectId,
});
const activeErrors = ref<string[]>([]);

const locale = computed(() => getDatepickerLocale());
const activeUsers = computed(() => CMSUserRepository.getAllExceptArchivedUsers());

function updateIsProjectLoading(newState: boolean) {
    return store.dispatch('projectLoadingState/updateIsProjectLoading', newState);
}

async function onSaveNextStep(shouldCreateChatMessage = true, chatMessageId?: string | null | undefined) {
    if (!isEditModeActive.value) {
        return Promise.reject();
    }
    updateIsProjectLoading(true);
    const chatMessageResponseId: string | null = await createNewChatMessageAndChat(
        shouldCreateChatMessage,
        props.projectId,
        props.chatId,
        nextStepObject.id,
    );

    const backendCompliantObject = transformObjectToBeBackendCompliant(
        nextStepObject as UpgradedNextStep,
        chatMessageId || chatMessageResponseId,
    );
    let nextStep;

    try {
        if (backendCompliantObject.id) {
            nextStep = await NextStep.updateExisting(backendCompliantObject.id, backendCompliantObject);
        } else {
            nextStep = await NextStep.createNew(backendCompliantObject);
        }
    } catch (error) {
        onError((error as Error).message);
        throw error;
    }

    showMessageAndDisableEditMode(vm.$t('Aktivnost je uspješno spremljena') as string);

    EventBus.$emit(EventBusEvents.updateProject);

    return Promise.resolve(nextStep);
}

async function updateEditMode(newState: boolean) {
    isEditModeActive.value = newState;
    setIsFullHeight(newState);

    if (newState) {
        await fetchDropdownOptions();
    }

    if (!newState) {
        emitDataChanges(false);
    }
}

function onCancel() {
    updateEditMode(false);
    setDynamicPropertiesToNextStepObject();
}

function toggleIsFullHeight() {
    setIsFullHeight(!isFullHeight.value);
}

function setIsFullHeight(newState: boolean) {
    isFullHeight.value = newState;
}

function onDropdownChange(value: string, property: string) {
    if (nextStepObject[property] === undefined) {
        return;
    }

    nextStepObject[property] = value;
    onInput();
}

function onNextStepTypeChange(value: string) {
    onDropdownChange(value, 'nextStepType');
}

function onReminderOptionChange(value: string) {
    onDropdownChange(value, 'reminderOption');
}

function onAssignedUserChange(value: string) {
    onDropdownChange(value, 'assignedUser');
}

function onUpdateContent(content: string) {
    nextStepObject.note = content;
    onInput();
}

function onInput() {
    emitDataChanges(true);
}

function emitDataChanges(state: boolean) {
    EventBus.$emit(EventBusEvents.changesInDataMade, {
        component: ProjectUnsavedDataComponents.NEXT_STEP_MODULE,
        state,
    });
}

function onConfirmDeleteOpen() {
    vm.$confirm({
        title: vm.$t('Jeste li sigurni da želite obrisati ovu aktivnost?'),
        content: '',
        onOk: onDeleteNextStep,
    });
}

async function onDeleteNextStep() {
    emit('onDeleteNextStep', nextStepObject.localKey);

    if (nextStepObject.id == null) {
        return;
    }

    try {
        await NextStep.deleteExisting(nextStepObject.id);
    } catch (error) {
        onError((error as Error).message);
        return;
    }

    showMessageAndDisableEditMode(vm.$t('Aktivnost je uspješno obrisana') as string);
}

function onError(message: string) {
    vm.$notification.error({
        message: vm.$t('Dogodila se greška') as string,
        description: message,
    });
    updateIsProjectLoading(false);
}

function showMessageAndDisableEditMode(message: string) {
    vm.$notification.success({
        message,
        description: ``,
    });
    updateEditMode(false);
}

function setDynamicPropertiesToNextStepObject() {
    if (props.singleNextStepData == null) {
        return;
    }

    Object.entries(props.singleNextStepData).forEach(([key, value]) => {
        vm.$set(nextStepObject, key, value);
    });
}

async function onCompleteNextStep() {
    if (isEditModeActive.value || nextStepObject.id == null || nextStepObject.completed == null) {
        return;
    }

    try {
        await NextStep.updateCompletedState(nextStepObject.id, nextStepObject.completed);
    } catch (error) {
        onError((error as Error).message);
        return;
    }

    showMessageAndDisableEditMode(vm.$t('Aktivnost je uspješno spremljena') as string);
}

async function fetchDropdownOptions() {
    try {
        await NextStepType.getAll();
        await ReminderOption.getAll();
    } catch (e) {
        vm.$notification.error({
            message: vm.$t('Dogodila se greška') as string,
            description: (e as Error).message,
        });
        return Promise.reject();
    }
    return Promise.resolve();
}

watch(
    () => props.singleNextStepData,
    async () => {
        if (props.singleNextStepData == null) {
            return;
        }

        const nextStepEntityExists = props.singleNextStepData.id != null;

        setDynamicPropertiesToNextStepObject();
        await updateEditMode(!nextStepEntityExists);
        if (!nextStepEntityExists && props.defaultNextStepTypeName != null) {
            const defaultNextStepType = NextStepTypeRepository.getByName(props.defaultNextStepTypeName);
            nextStepObject.nextStepType = defaultNextStepType ? defaultNextStepType.id : null;
        }
    },
    { immediate: true, deep: true },
);

expose({
    onSaveNextStep,
});

return { __sfc: true,props, emit, proxy, vm, isEditModeActive, isFullHeight, nextStepObject, activeErrors, locale, activeUsers, updateIsProjectLoading, onSaveNextStep, updateEditMode, onCancel, toggleIsFullHeight, setIsFullHeight, onDropdownChange, onNextStepTypeChange, onReminderOptionChange, onAssignedUserChange, onUpdateContent, onInput, emitDataChanges, onConfirmDeleteOpen, onDeleteNextStep, onError, showMessageAndDisableEditMode, setDynamicPropertiesToNextStepObject, onCompleteNextStep, fetchDropdownOptions, ChatRichTextEditor }
}

})